(ns manage.adset-decision-tree-page.view.navigation-panel
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["src/svg/chevron/chevron-down.svg$default" :as ChevronDown]
            ["src/svg/chevron/chevron-up.svg$default" :as ChevronUp]
            ["src/svg/controls/scope.svg$default" :as Scope]
            ["src/svg/files_folders/file-spreadsheet.svg$default" :as FileSpreadsheet]
            ["src/svg/location/poi.svg$default" :as POI]
            ["src/svg/objects/tag.svg$default" :as Tag]
            ["src/svg/organisations/other/meta.svg$default" :as Meta]
            [clojure.string :as str]
            [common.queries :refer [use-query]]
            [common.resource.query.decision-tree :as queries]
            [common.resource.query.poi :as poi-query]
            [manage.adset-decision-tree-page.ad-variant-table-ocp.columns :as ad-variant-cols]
            [manage.adset-decision-tree-page.editor.hooks :refer [use-fit-view]]
            [manage.adset-decision-tree-page.selection :as selection]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.tagging-and-trackers.tagging.db
             :as tagging-db]
            [manage.adset-decision-tree-page.view.context :as context]
            [manage.adset-decision-tree-page.view.hook :as hook]
            [manage.adset-decision-tree-page.view.predicate-editor.converter
             :as predicate-converter]
            [manage.adset-decision-tree-page.view.side-panel.ad-variants
             :refer [ad-variant-list variant-details-button]]
            [manage.adset-decision-tree-page.view.side-panel.feed.selector
             :refer [use-assigned-feed-name]]
            [manage.adset.hooks :refer [use-channel]]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui chip [{:keys [label on-select highlight id-to-fit-view]}]
  (let [fit-view (use-fit-view)]
    ($ Chip {:label label
             :on-click (fn []
                         (fit-view [(str id-to-fit-view)])
                         (on-select))
             :sx #js {:my 0.5
                      :mr 0.5
                      :bgcolor (when highlight "primary.selected")
                      :color (when highlight "primary.main")}})))

(defui summary-row [{:keys [summary icon]}]
  ($ Stack {:direction "row"
            :spacing 1
            :alignItems "center"}
     ($ SvgIcon {:component icon})
     ($ Typography {:color "text.secondary"} summary)))

(defui targeting-summary [{:keys [property]
                           {{:keys [predicate]} :targeting :keys [parent-id]} :node-data}]
  (let [{:keys [output]} (context/use-property-context property)
        parent-node (use-subscribe [::sub/node-data parent-id])
        predicate-summaries (predicate-converter/get-predicate-summaries output predicate parent-node)]
    (for [summary predicate-summaries]
      ($ summary-row {:summary summary
                      :icon Scope
                      :key summary}))))

(defhook use-ad-group-summary [adset-id]
  (let [url-context (use-subscribe [:url-context])
        channel (use-channel)
        {:keys [data]} (use-query
                        (case channel
                          "meta" (queries/fetch-meta-adsets-query url-context)
                          "tiktok" (queries/fetch-tiktok-ad-groups-query url-context)
                          nil))]
    {:label (case channel
              "meta" "Meta adset"
              "tiktok" "TikTok Ad Group"
              nil)
     :description
     (str (when-let [adset-name (some #(when (= (:id %) adset-id) (:description %)) data)]
            (str adset-name " - "))
          adset-id)}))

(defui meta-adset-summary [{:keys [node-data]}]
  (let [[value] (hook/use-selected-item-property
                 {:selected-item (selection/get-selected-item-for-node node-data)
                  :property-id :meta.facebook/adset-id})
        {:keys [label description]} (use-ad-group-summary value)]
    ($ summary-row {:summary (str label ": " description)
                    :icon Meta})))

(defn- time-constraint-label
  [{:keys [open-status closes-sooner-than closes-later-than
           opens-sooner-than opens-later-than]}]
  (cond
    closes-sooner-than (str "Closes within " closes-sooner-than " min")
    closes-later-than  (str "Closes after " closes-later-than " min")
    opens-sooner-than  (str "Opens within " opens-sooner-than " min")
    opens-later-than   (str "Opens after " opens-later-than " min")
    open-status        (str "Status: " open-status)))

(defui poi-strategy-summary [{:keys [node-data]}]
  (let [{:keys [selector-id max-radius-km min-radius-km]
         :as param} (get-in node-data [:targeting :context 0 :param])
        {:keys [advertiser-id]} (use-subscribe [:url-context])
        {:keys [data]} (use-query (poi-query/selector-query
                                   {:advertiser-id advertiser-id
                                    :selector-id selector-id}))
        selector-name (when data (poi-query/selector-label data))
        distance (if min-radius-km
                   (str min-radius-km "–" max-radius-km " km")
                   (str "up to " max-radius-km " km"))
        time-label (time-constraint-label param)]
    (when selector-id
      ($ :<>
         ($ summary-row {:summary (str "Selector: " selector-name) :icon POI})
         (when max-radius-km
           ($ summary-row {:summary (str "Distance: " distance) :icon POI}))
         (when time-label
           ($ summary-row {:summary time-label :icon POI}))))))

(defn tagging->summary [tagging]
  (when (seq tagging)
    (tagging-db/params->url (mapv (fn [{:keys [name values]}] [name (first values)]) tagging))))

(defui meta-config-summary [{:keys [adset-id]}]
  (let [{:keys [label description]} (use-ad-group-summary adset-id)]
    ($ summary-row {:summary (str label ": " description)
                    :icon Meta})))

(defn tracker->summary [type value]
  (str (str/capitalize type) " tracker: " value))

(defhook use-root-summary []
  (let [channel (use-channel)
        {{{meta-adset-id :facebook-adset-id} :meta
          :keys [click-through-url-tagging trackers pixels]} :config}
        (use-subscribe [::sub/root-node-data])
        meta-channel (= channel "meta")
        show-meta (and meta-adset-id meta-channel)
        utm (tagging->summary click-through-url-tagging)
        pixel-id (-> pixels first :value)
        show-pixel (and meta-channel (not (str/blank? pixel-id)))]
    {:show-meta show-meta
     :meta-adset-id meta-adset-id
     :utm utm
     :trackers trackers
     :show-pixel show-pixel
     :pixel-id pixel-id
     :visible (or show-meta utm (seq trackers) show-pixel)}))

(defui root-summary [{:keys [show-meta meta-adset-id utm trackers show-pixel pixel-id]}]
  ($ Stack {:spacing 1}
     ($ Typography {:color "text.subtle"
                    :sx #js {:textTransform "uppercase"}}
        "Defaults")
     (when show-meta
       ($ meta-config-summary {:adset-id meta-adset-id}))
     (when utm
       ($ summary-row {:summary (str "UTM tagging: " utm)
                       :icon Tag}))
     (for [{:keys [type value]} trackers]
       ($ summary-row {:key (str type value)
                       :summary (tracker->summary type value)
                       :icon Tag}))
     (when show-pixel
       ($ summary-row {:summary (str "Meta Pixel: " pixel-id)
                       :icon Meta}))))

(defn feed-input->summary [{:keys [from to]}]
  (str to " = " (or (get-in from [:param :value]) (:selector from))))

(defui feed-summary [{:keys [node-data]}]
  (let [{:keys [value-column] {:keys [feed-id data]} :param} (get-in node-data [:targeting :context 0])
        feed-name (use-assigned-feed-name feed-id)]
    (when feed-id
      ($ :<>
         ($ summary-row {:summary (str "Feed: " (or feed-name feed-id))
                         :icon FileSpreadsheet})
         (for [{:keys [to] :as input} (:in data)]
           ($ summary-row {:key to
                           :summary (feed-input->summary input)
                           :icon FileSpreadsheet}))
         (when value-column
           ($ summary-row {:summary (str "Targeting column: " value-column)
                           :icon FileSpreadsheet}))))))

(def property-id->summary
  {:targeting/predicate targeting-summary
   :targeting/poi-predicate targeting-summary
   :meta.facebook/adset-id meta-adset-summary
   :poi/properties poi-strategy-summary
   :feed/properties feed-summary})

(defui timezone-summary [{:keys [node-data]}]
  (let [configured-timezone (or (get-in node-data [:config :meta :timezone])
                                (get-in node-data [:config :tiktok :timezone]))]
    (when configured-timezone
      ($ summary-row {:summary (str "Timezone: " configured-timezone)
                      :icon Tag}))))

(defui property-summaries [{:keys [node-data]}]
  ($ :<>
     (for [{:keys [id] :as property} (:properties node-data)
           :let [summary-component (property-id->summary id)]
           :when (some? summary-component)]
       ($ summary-component {:key id
                             :node-data node-data
                             :property property}))))

(defui outcome-property-summary [{:keys [id]}]
  ($ property-summaries {:node-data (use-subscribe [::sub/node-data id])}))

(defui outcome-chip [{:keys [id highlight]}]
  (let [{:keys [label parent-id] :as node-data} (use-subscribe [::sub/node-data id])
        {:keys [on-select]} (hook/use-node-outcome-control node-data)]
    ($ chip {:label label
             :on-select on-select
             :highlight highlight
             :id-to-fit-view parent-id})))

(defhook use-default-outcome-chip-props
  [{:keys [selected-item decision-node]}]
  (let [default-outcome-node-control (hook/use-default-outcome-node-control decision-node)
        highlight (and (= (selection/get-host-node-id selected-item) (:id decision-node))
                       (= (:type selected-item) :selection/default-outcome-node))]
    (when default-outcome-node-control
      (assoc default-outcome-node-control
             :label "Default"
             :highlight highlight
             :id-to-fit-view (:id decision-node)))))

(defui outcome-chip-collapse [{:keys [chip-props]}]
  (let [[expanded set-expanded] (uix/use-state false)
        [first-10-props rest-props] (split-at 10 chip-props)
        highlighted-in-rest (first (filter :highlight rest-props))
        first-10-props (if highlighted-in-rest
                         (conj (vec (butlast first-10-props)) highlighted-in-rest)
                         first-10-props)]
    ($ Stack {:direction "row"
              :sx #js {:flexWrap "wrap"}}
       (for [{{:keys [category]} :node :as props} (if expanded chip-props first-10-props)
             :let [component (if (= category :default-outcome-node) chip outcome-chip)]]
         ($ component props))
       (when (seq rest-props)
         ($ Button {:sx #js {:color "primary.main"
                             :ml "auto"}
                    :onClick (fn [e]
                               (set-expanded not)
                               (.stopPropagation e))
                    :endIcon ($ SvgIcon {:component (if expanded ChevronUp ChevronDown)})}
            (if expanded "Show less" "Show all"))))))

(defui decision [{{decision-name :name :keys [outcome-node-ids] :as decision-node} :decision-node
                  {outcome-id :id} :outcome-node :as props}]
  (let [default-outcome-chip-props (use-default-outcome-chip-props props)
        chip-props (cond-> (mapv (fn [id] {:id id :key id :highlight (= id outcome-id)})
                                 outcome-node-ids)
                     default-outcome-chip-props (conj default-outcome-chip-props))]
    ($ Stack {:spacing 1}
       ($ Typography {:color "text.subtle"
                      :sx #js {:textTransform "uppercase"}}
          decision-name)
       ($ outcome-chip-collapse {:chip-props chip-props})
       ($ timezone-summary {:node-data decision-node})
       ($ property-summaries {:node-data decision-node})
       ($ outcome-property-summary {:id outcome-id}))))

(defui ad-variant-section [{:keys [node-id] :as list-controls}]
  (let [uuids (->> list-controls :ad-variants (map :uuid))
        selection (set uuids)
        row-ids (map #(ad-variant-cols/row-id node-id %) uuids)]
    ($ Stack {:spacing 1}
       ($ Stack {:direction "row"
                 :justifyContent "space-between"
                 :alignItems "center"}
          ($ Typography {:color "text.subtle"} "AD VARIANT")
          ($ Stack {:direction "row"
                    :alignItems "center"
                    :spacing 1}
             ($ variant-details-button {:variant-row-ids row-ids})
             ($ Button
                {:variant "contained"
                 :onClick #(rf/dispatch [:manage.ad-variant-preview.db/open-preview selection false])}
                "Preview")))
       ($ ad-variant-list list-controls))))

(defhook use-child-decision-node [{:keys [selected-item]}]
  (let [node-id->children (use-subscribe [::sub/node-id->children])
        selected-node-id (selection/get-host-node-id selected-item)
        root-node-id (use-subscribe [::sub/root-node-id])]
    (uix/use-memo #(first (get node-id->children (or selected-node-id root-node-id)))
                  [node-id->children selected-node-id root-node-id])))

(defui panel [{:keys [full-page]}]
  (let [{[_root & path] :nodes :keys [selected-item]} (use-subscribe [::sub/selection-path])
        ascendant-decision-outcome-pairs (partition-all 2 path)
        child-node (use-child-decision-node {:selected-item selected-item})
        div-bottom-ref (uix/use-ref nil)
        {:keys [visible] :as root-summary-data} (use-root-summary)
        {:keys [any-variants-assigned] :as list-controls} (hook/use-ad-variant-controls
                                                           {:selected-item selected-item})]
    (uix/use-effect #(when-let [current @div-bottom-ref]
                       (.scrollIntoView current #js {:behavior "smooth"}))
                    [selected-item])
    ($ Box {:sx #js {:overflowY "auto"
                     :mr (when full-page 4)}}
       ($ Stack {:spacing 2
                 :divider ($ Divider)
                 :sx #js {:pl 4 :py 2}}
          (when visible
            ($ root-summary root-summary-data))
          (for [[decision-node outcome-node] ascendant-decision-outcome-pairs]
            ($ decision {:key (:id decision-node)
                         :decision-node decision-node
                         :outcome-node outcome-node
                         :selected-item selected-item}))
          (when (= :decision-node (:category child-node))
            ($ decision {:decision-node child-node}))
          (when any-variants-assigned
            ($ ad-variant-section (assoc list-controls
                                         :node-id (selection/get-host-node-id selected-item)))))
       ($ Box {:ref div-bottom-ref
               :sx #js {:height "1rem"}}))))
